import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BriefcaseBusiness, CheckCircle2, Users } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import {
  acceptPartnerTeamInvitation,
  listMyTeamInvitations,
} from "@/lib/partner-business.functions";

export const Route = createFileRoute("/_authenticated/team-invitations")({
  head: () => ({
    meta: [
      { title: "Business team invitations — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TeamInvitationsPage,
});

function TeamInvitationsPage() {
  const client = useQueryClient();
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const invites = useQuery({
    queryKey: ["my-team-invitations"],
    queryFn: () => listMyTeamInvitations(),
  });

  async function accept(memberId: string) {
    if (acceptingId) return;
    setAcceptingId(memberId);
    try {
      const result = await acceptPartnerTeamInvitation({ data: { memberId } });
      if (!result.ok) {
        toast.error(result.error ?? "Could not accept this team invitation");
        return;
      }
      toast.success("Business team invitation accepted");
      await client.invalidateQueries({ queryKey: ["my-team-invitations"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not accept this team invitation");
    } finally {
      setAcceptingId(null);
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <AppHeader workspace="customer" />
      <main className="product-page section-pad mx-auto py-8">
        <header className="max-w-2xl">
          <p className="dashboard-eyebrow !text-primary">SHIELDS BUSINESS</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#07164f]">Team invitations</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Invitations only activate when you are signed in with the email address that was invited.
          </p>
        </header>

        {invites.isLoading ? (
          <p className="mt-8 text-sm text-muted-foreground">Checking invitations…</p>
        ) : (invites.data ?? []).length === 0 ? (
          <section className="card-surface mt-8 p-6 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8 text-success" />
            <h2 className="mt-3 font-display text-lg font-bold">No pending invitations</h2>
            <p className="mt-1 text-sm text-muted-foreground">You are all caught up.</p>
            <Link to="/" className="mt-4 inline-flex text-sm font-bold text-primary">Return to Shields</Link>
          </section>
        ) : (
          <section className="mt-8 space-y-3">
            {(invites.data ?? []).map((invite: any) => (
              <article key={invite.id} className="card-surface flex flex-wrap items-center justify-between gap-4 p-5">
                <div className="flex items-start gap-3">
                  <span className="os-module-icon"><Users /></span>
                  <div>
                    <strong className="block">{invite.display_name || "Business team invitation"}</strong>
                    <p className="text-sm text-muted-foreground">
                      Role: {invite.business_role} · invited to a Shields Business account
                    </p>
                  </div>
                </div>
                <Button disabled={Boolean(acceptingId)} onClick={() => accept(invite.id)}>
                  <BriefcaseBusiness className="mr-2 h-4 w-4" /> {acceptingId === invite.id ? "Accepting…" : "Accept"}
                </Button>
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}
